import { DestroyRef, Directive, effect, ElementRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GlobalEventsService, OnAttributeMutation, OnChildListMutations } from '@coin/shared/core/util-helpers';

/**
 * Adds keyboard navigation to menu-type containers. It selects all elements given by the `coinKeyboardMenuNavigation` input parameter and let's you cycle among them.
 *
 * You need to have an `aria-hidden` property on the containing element and set it to false to work. While an element is hidden, the keys aren't processed.
 *
 * @property {string} coinKeyboardMenuNavigation CSS selector
 * @property closeMenu emitted when the user presses the Escape key.
 *
 * @example
 * ```html
 * <div [ariaHidden]="!showFooter()" coinKeyboardMenuNavigation="[role='menuitem']">
 * ```
 */
@Directive({
  selector: '[coinKeyboardMenuNavigation]',
  host: {
    '(click)': 'onClicked($event)',
    '(keydown)': 'handleKeyboardEvent($event)'
  }
})
export class KeyboardMenuNavigationDirective {
  private containerEle = inject<ElementRef<HTMLElement>>(ElementRef);

  /**
   * CSS selector to match the items to cycle.
   *
   * @example [role='menuitem']
   */
  coinKeyboardMenuNavigation = input('');
  navigationRows = input(1);

  /**
   * Is emitted when the user presses the Escape key
   */
  closeMenu = output();

  private currentIndex = signal(-1);
  private menuItems = signal<HTMLElement[]>([]);
  private isHidden = signal(true);
  private destroyRef = inject(DestroyRef);
  private globalEventsService = inject(GlobalEventsService);

  constructor() {
    const { containerEle } = this;

    effect(() => {
      if (this.menuItems()[this.currentIndex()]) {
        this.menuItems()[this.currentIndex()].focus();
      }
    });

    effect(() => {
      if (this.isHidden()) {
        this.currentIndex.set(-1);
      }
    });

    effect(() => this.isHidden.set(containerEle.nativeElement.getAttribute('aria-hidden') === 'true'));

    OnAttributeMutation(containerEle.nativeElement)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(mr => {
        if (mr.attributeName === 'aria-hidden') {
          this.isHidden.set(containerEle.nativeElement.getAttribute('aria-hidden') === 'true');
        }
      });

    OnChildListMutations(containerEle.nativeElement)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() =>
        this.menuItems.set(
          Array.from(containerEle.nativeElement.querySelectorAll(this.coinKeyboardMenuNavigation())) as HTMLElement[]
        )
      );

    this.globalEventsService
      .listen('focusin')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!containerEle.nativeElement.contains(document.activeElement)) {
          this.currentIndex.set(-1);
        } else {
          const eleIndex = this.menuItems().findIndex(e => e === document.activeElement);
          if (eleIndex > -1) {
            this.currentIndex.set(eleIndex);
          }
        }
      });
  }

  onClicked(event: MouseEvent) {
    const target = event.target as HTMLElement;

    if (this.containerEle.nativeElement === target) {
      this.currentIndex.set(0);
    }
  }

  private keyboardActions: Record<string, (event: KeyboardEvent) => void> = {
    /* eslint-disable @typescript-eslint/naming-convention */
    ArrowRight: () => this.moveFocus(1),
    ArrowLeft: () => this.moveFocus(-1),
    ArrowUp: () => this.moveFocus(-this.navigationRows()),
    ArrowDown: () => this.moveFocus(+this.navigationRows()),
    Home: () => this.focusFirst(),
    End: () => this.focusLast(),
    Escape: () => this.closeMenu.emit(),
    Tab: event => this.handleTab(event)
    /* eslint-enable @typescript-eslint/naming-convention */
  };

  handleKeyboardEvent(event: KeyboardEvent) {
    if (!this.menuItems().length || this.isHidden()) return;

    const action = this.keyboardActions[event.key];

    if (action) {
      action(event);
    }
  }

  private handleTab(event: KeyboardEvent) {
    if (event.shiftKey) {
      if (this.currentIndex() > 0) {
        event.preventDefault();
        this.moveFocus(-1);
      }
    } else if (this.currentIndex() < this.menuItems().length - 1) {
      event.preventDefault();
      this.moveFocus(1);
    }
  }

  private moveFocus(indexChange: number) {
    const itemsLength = this.menuItems().length;
    let newIndex = this.currentIndex() + indexChange;

    if (newIndex === itemsLength) newIndex -= itemsLength;
    if (newIndex > itemsLength) newIndex -= itemsLength + 1;
    if (newIndex === -1) newIndex += itemsLength;
    if (newIndex < 0) newIndex += itemsLength + 1;

    this.currentIndex.set(newIndex);
  }

  private focusFirst() {
    this.currentIndex.set(0);
  }

  private focusLast() {
    this.currentIndex.set(this.menuItems().length - 1);
  }
}
